import PropTypes from 'prop-types';
import { forwardRef } from 'react';

import { ButtonMain, IconAddon, LoadingAddon, SROnly } from './Button.styled';

const Button = forwardRef(function Button(
  { children, variant, tone, size, shadow, isLoading, wide, IconBefore, IconAfter, href, ...props },
  ref
) {
  const elAttrs = href ? { href, as: 'a' } : { type: 'button' };

  const disabledProps = {
    'aria-disabled': 'true',
    onClick: (e) => {
      // aria-disabled dos not prevent interaction
      // as the disabled attr
      e.preventDefault();
    },
  };
  const loadingAttrs = isLoading ? disabledProps : {};
  const disabledAttrs = props.disabled ? disabledProps : {};

  return (
    <ButtonMain
      ref={ref}
      $size={size}
      $variant={variant}
      $tone={tone}
      $shadow={shadow}
      $isLoading={isLoading}
      $wide={wide}
      {...elAttrs}
      {...props}
      {...loadingAttrs}
      {...disabledAttrs}
    >
      {IconBefore && <IconAddon as={IconBefore} $place="before" />}
      {children}
      {isLoading && (
        <>
          <SROnly aria-live="polite">Loading</SROnly>
          <LoadingAddon />
        </>
      )}
      {IconAfter && <IconAddon as={IconAfter} $place="after" />}
    </ButtonMain>
  );
});

export default Button;

Button.defaultProps = {
  variant: 'solid',
  tone: 'primary',
  size: 'md',
};

Button.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  /** Defines the button overall layout */
  variant: PropTypes.oneOf([
    /** the button with a background */
    'solid',
    /** the button with border */
    'outline',
    /** the button without bg or border */
    'ghost',
    /** Same as ghost but all in uppercase */
    'ghost-caps',
  ]),
  /** Defines the button color scheme */
  tone: PropTypes.oneOf(['primary', 'secondary', 'destructive', 'subtle', 'inverted']),
  /** Defines the button padding and font-size */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  /** Adds a shadow based on button tone. solid variant only */
  shadow: PropTypes.bool,
  /** Displays a spinner and prevents interaction */
  isLoading: PropTypes.bool,
  /** When provided it renders an anchor instead of button element */
  href: PropTypes.string,
  /** Icon SVG (component) as prefix */
  IconBefore: PropTypes.elementType,
  /** Icon SVG (component) as suffix */
  IconAfter: PropTypes.elementType,
};
